import { TFunction } from "i18next";

import {
  IBarGraphContent,
  IFlexGroupingColumns,
  IReportColumn,
  IReportGridColumn,
  ITableDto,
  PERIOD_GROUPING_FIELD_NAME,
} from "@ryde/shared";

import {
  METRIC_TOTAL_TYPES,
  REPORT_CHART_GROUPING_DIMENSION_VALUE,
  REPORT_CHART_GROUPING_PART_SEPARATOR,
} from "./report-chart.constants";
import { IReportChartAxisOption } from "./report-chart.models";

const findColumnByKey = (
  columns: IReportGridColumn[],
  key: string,
): IReportGridColumn | undefined =>
  columns.find(
    col => col.uiTableColumn === key || col.columnTemplateKey === key,
  );

const getFixedReportDataGroupingColumns = (
  columns: IReportGridColumn[],
): IReportGridColumn[] =>
  columns.filter(
    col =>
      !col.isSummaryColumn &&
      !col.isHiddenFromView &&
      !col.isIdColumn &&
      !col.isPeriodGroupingColumn,
  );

export const getReportGroupingDimensionColumns = ({
  columns,
  groupingParamKeys,
  isFlexPeriodApplied,
  hasFlexGroupingTemplate,
}: {
  columns: IReportGridColumn[];
  groupingParamKeys: string[];
  isFlexPeriodApplied: boolean;
  hasFlexGroupingTemplate: boolean;
}): IReportGridColumn[] => {
  const result: IReportGridColumn[] = [];

  if (isFlexPeriodApplied) {
    const periodColumn = columns.find(col => col.isPeriodGroupingColumn);
    if (periodColumn) {
      result.push(periodColumn);
    }
  }

  groupingParamKeys.forEach(key => {
    const column = findColumnByKey(columns, key);
    if (
      column &&
      !result.some(col => col.uiTableColumn === column.uiTableColumn)
    ) {
      result.push(column);
    }
  });

  if (hasFlexGroupingTemplate) {
    return result;
  }

  const dataColumns = getFixedReportDataGroupingColumns(columns);

  if (!result.length) {
    return dataColumns;
  }

  const dataColumnsWithoutPeriod = dataColumns.filter(
    col => !result.some(r => r.uiTableColumn === col.uiTableColumn),
  );

  return [...result, ...dataColumnsWithoutPeriod];
};

export const parseReportGroupingCompositeKey = (
  compositeKey: string,
): string[] => compositeKey.split(REPORT_CHART_GROUPING_PART_SEPARATOR);

export const aggregateReportRowsByReportGrouping = ({
  rows,
  groupingColumns,
  metricColumnKey,
}: {
  rows: ITableDto[];
  groupingColumns: IReportGridColumn[];
  metricColumnKey: string;
}): IBarGraphContent => {
  if (!groupingColumns.length) {
    return { entities: [] };
  }

  const grouped = new Map<string, number>();

  rows.forEach(row => {
    const parts = groupingColumns.map(col =>
      String(row[col.uiTableColumn] ?? ""),
    );

    if (parts.every(part => !part)) {
      return;
    }

    const dimensionValue = parts.join(REPORT_CHART_GROUPING_PART_SEPARATOR);
    const rawMetric = row[metricColumnKey];
    const metricValue =
      typeof rawMetric === "number" ? rawMetric : Number(rawMetric);

    if (Number.isNaN(metricValue)) {
      return;
    }

    grouped.set(
      dimensionValue,
      (grouped.get(dimensionValue) ?? 0) + metricValue,
    );
  });

  return {
    entities: Array.from(grouped.entries()).map(([subLabel, value]) => ({
      subLabel,
      value,
    })),
  };
};

export const aggregateReportRows = ({
  rows,
  dimensionColumnKey,
  metricColumnKey,
}: {
  rows: ITableDto[];
  dimensionColumnKey: string;
  metricColumnKey: string;
}): IBarGraphContent => {
  const grouped = new Map<string, number>();

  rows.forEach(row => {
    const dimensionValue = String(row[dimensionColumnKey] ?? "");
    const rawMetric = row[metricColumnKey];
    const metricValue =
      typeof rawMetric === "number" ? rawMetric : Number(rawMetric);

    if (!dimensionValue || Number.isNaN(metricValue)) {
      return;
    }

    grouped.set(
      dimensionValue,
      (grouped.get(dimensionValue) ?? 0) + metricValue,
    );
  });

  return {
    entities: Array.from(grouped.entries()).map(([subLabel, value]) => ({
      subLabel,
      value,
    })),
  };
};

export const resolveColumnAxisLabel = (
  col: IReportGridColumn,
  t: TFunction,
): string => {
  if (col.columnTitleTranslationKey) {
    return t(col.columnTitleTranslationKey);
  }

  if (col.translationKey) {
    return t(col.translationKey);
  }

  return t(`gridLabel.${col.uiTableColumn}`);
};

export const mapColumnToAxisOption = (
  col: IReportGridColumn,
  t: TFunction,
): IReportChartAxisOption => ({
  value: col.uiTableColumn,
  label: resolveColumnAxisLabel(col, t),
});

export const mapFlexGroupingTemplateToAxisOption = ({
  columnKey,
  col,
  t,
}: {
  columnKey: string;
  col: IReportColumn;
  t: TFunction;
}): IReportChartAxisOption => ({
  value: columnKey,
  label: resolveColumnAxisLabel(
    {
      ...col,
      columnTemplateKey: columnKey,
      uiTableColumn: columnKey,
      isSummaryColumn: false,
      isFlexGroupingColumn: true,
      translationKey: "",
      fieldExpression: "",
    },
    t,
  ),
});

export const buildReportChartDimensionOptions = ({
  columns,
  groupingParamKeys,
  flexGroupingTemplateKeys,
  flexGroupingColumns,
  periodGroupingColumn,
  hasReportGroupingDimension,
  t,
}: {
  columns: IReportGridColumn[];
  groupingParamKeys: string[];
  flexGroupingTemplateKeys: string[];
  flexGroupingColumns?: IFlexGroupingColumns;
  periodGroupingColumn?: IReportColumn;
  hasReportGroupingDimension: boolean;
  t: TFunction;
}): IReportChartAxisOption[] => {
  const optionsByValue = new Map<string, IReportChartAxisOption>();

  columns
    .filter(col =>
      isDimensionColumn(col, groupingParamKeys, flexGroupingTemplateKeys),
    )
    .forEach(col => {
      const option = mapColumnToAxisOption(col, t);
      optionsByValue.set(option.value, option);
    });

  if (flexGroupingColumns) {
    flexGroupingTemplateKeys.forEach(key => {
      if (optionsByValue.has(key)) {
        return;
      }

      const templateColumn = flexGroupingColumns[key];

      if (templateColumn) {
        optionsByValue.set(
          key,
          mapFlexGroupingTemplateToAxisOption({
            columnKey: key,
            col: templateColumn,
            t,
          }),
        );
      }
    });
  }

  if (periodGroupingColumn && !optionsByValue.has(PERIOD_GROUPING_FIELD_NAME)) {
    optionsByValue.set(
      PERIOD_GROUPING_FIELD_NAME,
      mapFlexGroupingTemplateToAxisOption({
        columnKey: PERIOD_GROUPING_FIELD_NAME,
        col: periodGroupingColumn,
        t,
      }),
    );
  }

  const result = Array.from(optionsByValue.values());

  if (hasReportGroupingDimension) {
    result.push({
      value: REPORT_CHART_GROUPING_DIMENSION_VALUE,
      label: t("reports.chart.REPORT_GROUPING"),
    });
  }

  return result;
};

export const shouldSyncReportGroupingForChartDimension = ({
  dimensionKey,
  groupingParamKeys,
  flexGroupingTemplateKeys,
  isFlexPeriodApplied,
}: {
  dimensionKey: string;
  groupingParamKeys: string[];
  flexGroupingTemplateKeys: string[];
  isFlexPeriodApplied: boolean;
}): {
  appendFlexGroupingKey?: string;
  enablePeriodGrouping?: boolean;
} | null => {
  if (dimensionKey === REPORT_CHART_GROUPING_DIMENSION_VALUE) {
    return null;
  }

  if (dimensionKey === PERIOD_GROUPING_FIELD_NAME) {
    if (!isFlexPeriodApplied) {
      return { enablePeriodGrouping: true };
    }

    return null;
  }

  if (
    flexGroupingTemplateKeys.includes(dimensionKey) &&
    !groupingParamKeys.includes(dimensionKey)
  ) {
    return { appendFlexGroupingKey: dimensionKey };
  }

  return null;
};

export const isMetricColumn = (col: IReportGridColumn): boolean => {
  if (col.isHiddenFromView) {
    return false;
  }

  if (col.isSummaryColumn) {
    return !col.totalType || METRIC_TOTAL_TYPES.includes(col.totalType);
  }

  if (!col.totalType || !METRIC_TOTAL_TYPES.includes(col.totalType)) {
    return false;
  }

  return !col.isFlexGroupingColumn;
};

export const isDimensionColumn = (
  col: IReportGridColumn,
  groupingParamKeys: string[],
  flexGroupingTemplateKeys: string[],
): boolean => {
  if (isMetricColumn(col)) {
    return false;
  }

  if (col.isFlexGroupingColumn || col.isPeriodGroupingColumn) {
    return true;
  }

  const columnKeys = [col.columnTemplateKey, col.uiTableColumn];

  if (
    groupingParamKeys.some(key => columnKeys.includes(key)) ||
    flexGroupingTemplateKeys.some(key => columnKeys.includes(key))
  ) {
    return true;
  }

  if (col.isHiddenFromView || col.isIdColumn) {
    return false;
  }

  return !col.isSummaryColumn;
};
